import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EMPTY, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, withLatestFrom } from "rxjs/operators";

import { OnOrbitService } from "../../../services/api/on-orbit.service";
import { OnOrbitActions } from "../../actions/on-orbit.actions";
import { FailState } from "../../models/fail-state";
import { AppState } from "../../reducers";
import { OnOrbitSelectors } from "../../selectors/on-orbit.selectors";

@Injectable()
export class OnOrbitEffects {
	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(OnOrbitActions.onorbit.getApi),
			switchMap(({ params }) =>
				this.onOrbitService.onorbit(params).pipe(
					map((onorbit) => OnOrbitActions.onorbit.getSuccess({ params, onorbit: onorbit ?? [] })),
					catchError((error) => of(OnOrbitActions.onorbit.getFail(new FailState(error))))
				)
			)
		)
	);

	lookup = createEffect(() =>
		this.actions.pipe(
			ofType(OnOrbitActions.onorbit.lookup),
			withLatestFrom(
				this.store.select(OnOrbitSelectors.cacheBySatNo),
				this.store.select(OnOrbitSelectors.unresolvedSatNos)
			),
			mergeMap(([{ satNos }, cache, unresolved]) => {
				const skip = new Set(unresolved);
				const missing = [...new Set(satNos)].filter(
					(satNo) => satNo != null && !cache.has(satNo) && !skip.has(satNo)
				);
				return missing.length === 0 ? EMPTY : of(OnOrbitActions.onorbit.lookupApi({ satNos: missing }));
			})
		)
	);

	lookupApi = createEffect(() =>
		this.actions.pipe(
			ofType(OnOrbitActions.onorbit.lookupApi),
			mergeMap(({ satNos }) =>
				this.onOrbitService.bySatNos(satNos).pipe(
					map((onorbit) => OnOrbitActions.onorbit.lookupSuccess({ onorbit: onorbit ?? [], requested: satNos })),
					catchError((error) => of(OnOrbitActions.onorbit.lookupFail(new FailState(error))))
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private store: Store<AppState>,
		private onOrbitService: OnOrbitService
	) {}
}
